import { Image } from "expo-image";
import { useContext } from "react";
import { View, XStack, YStack } from "tamagui";

import CuraiLogo from "@/assets/images/landing_page/general_care/curai-logo.png";
import CuraiLogoDark from "@/assets/images/landing_page/general_care/curai-logo-dark.png";
import DoctorImage from "@/assets/images/landing_page/general_care/md-female.png";
import { Button } from "@/components/Button";
import { LayoutContext } from "@/components/LayoutProvider";
import { Text } from "@/components/Typography";
import { usePropagatedRouter } from "@/hooks/usePropagatedRouter";
import { useIsDarkTheme } from "@/theme/helpers";

type ClinicianCtaProps = {
  title?: string;
  body?: string;
  ctaCopy?: string;
  onPress?: () => void;
  disabled?: boolean;
  showWatermark?: boolean;
};

export default function ClinicianCta({
  title = "Connect to clinicians within minutes",
  body = "Our AI will gather your medical history before connecting to a licensed clinician in your state, making your care quick and convenient.",
  ctaCopy = "Start your visit →",
  onPress,
  disabled,
  showWatermark = false,
}: ClinicianCtaProps) {
  const router = usePropagatedRouter();
  const handlePress = onPress ?? (() => router.push("/sign-in"));
  const { lg } = useContext(LayoutContext);
  const isDark = useIsDarkTheme();

  return (
    <YStack width="100%" backgroundColor="$fillCuraiSubtle" overflow="hidden">
      {showWatermark && (
        <View
          position="absolute"
          pointerEvents="none"
          width={400}
          height={400}
          left="50%"
          top={lg ? "50%" : undefined}
          bottom={lg ? undefined : -30}
          style={
            lg
              ? { transform: [{ translateX: -200 }, { translateY: -200 }] }
              : { transform: [{ translateX: -200 }] }
          }
        >
          <Image
            source={isDark ? CuraiLogoDark : CuraiLogo}
            style={{ width: "100%", height: "100%" }}
            contentFit="contain"
          />
        </View>
      )}

      <YStack
        maxWidth="$landingPageConstraint"
        alignSelf="center"
        paddingHorizontal="$250"
        paddingTop="$300"
      >
        <XStack
          width="100%"
          alignSelf="center"
          gap="$600"
          flexDirection={lg ? "row-reverse" : "column"}
          alignItems="center"
        >
          <YStack
            flex={1}
            width="100%"
            gap="$200"
            alignItems={lg ? "flex-start" : "center"}
          >
            <Text size="$xxl" textAlign={lg ? undefined : "center"}>
              {title}
            </Text>
            <Text
              size="$md"
              color="$textSecondary"
              textAlign={lg ? undefined : "center"}
            >
              {body}
            </Text>
            <Button
              mode="secondary"
              alignSelf={lg ? "flex-start" : "center"}
              onPress={handlePress}
              disabled={disabled}
            >
              {ctaCopy}
            </Button>
          </YStack>

          <View flex={1} width="100%" height={lg ? 400 : 300}>
            <Image
              source={DoctorImage}
              style={{ width: "100%", height: "100%" }}
              contentFit="contain"
            />
          </View>
        </XStack>
      </YStack>
    </YStack>
  );
}
